import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import type { ProjectItem } from '../data/projectsData';

interface Props {
  project: ProjectItem;
}

export default function ProjectDetail({ project: p }: Props) {
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState<string | null>(null);
  const { ref: infoRef, isVisible: infoVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: galleryRef, isVisible: galleryVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: highlightRef, isVisible: highlightVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <main>
      {/* Hero */}
      <section className="relative w-full h-[520px] overflow-hidden">
        <img
          src={p.image}
          alt={p.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-px bg-[#B8960C]"></div>
            <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Project Detail</span>
            <div className="w-8 h-px bg-[#B8960C]"></div>
          </div>
          <span className="inline-block bg-[#B8960C] text-white text-xs px-4 py-1.5 tracking-widest mb-4">
            {p.category}
          </span>
          <h1
            className="text-3xl lg:text-5xl font-bold text-white mb-4 leading-tight max-w-3xl"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            {p.title}
          </h1>
          <div className="flex items-center gap-6 text-white/70 text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-map-pin-line text-[#B8960C]"></i>
              {p.location}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-[#B8960C]"></i>
              {p.year}年竣工
            </span>
            {p.area && (
              <span className="flex items-center gap-1.5">
                <i className="ri-ruler-line text-[#B8960C]"></i>
                {p.area}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 mt-8">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 border border-white/50 text-white px-6 py-3 text-sm tracking-wider hover:border-[#B8960C] hover:text-[#B8960C] transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              施工実績一覧
            </button>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-[#B8960C] text-white px-6 py-3 text-sm tracking-wider hover:bg-[#9A7D0A] transition-all cursor-pointer whitespace-nowrap"
            >
              無料相談・お見積り
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-[#F8F6F1] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-8 py-3 flex items-center gap-2 text-xs text-gray-400">
          <a href="/" className="hover:text-[#B8960C] transition-colors cursor-pointer">ホーム</a>
          <i className="ri-arrow-right-s-line"></i>
          <a href="/#projects" className="hover:text-[#B8960C] transition-colors cursor-pointer">施工実績</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-[#B8960C]">{p.title}</span>
        </div>
      </div>

      {/* Project Info + Description */}
      <section className="py-20 bg-white">
        <div
          ref={infoRef as React.RefObject<HTMLDivElement>}
          className="max-w-7xl mx-auto px-8"
          style={{
            opacity: infoVisible ? 1 : 0,
            transform: infoVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Description */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-px bg-[#B8960C]"></div>
                <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Overview</span>
              </div>
              <h2
                className="text-2xl lg:text-3xl font-bold text-[#1A1A1A] mb-6"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                施工概要
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">{p.description}</p>

              {/* Related Service Link */}
              <a
                href={`/services/${p.relatedServiceSlug}`}
                className="inline-flex items-center gap-2 border border-[#B8960C] text-[#B8960C] px-5 py-2.5 text-sm tracking-wider hover:bg-[#B8960C] hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line"></i>
                </div>
                {p.relatedService}の詳細を見る
              </a>
            </div>

            {/* Info Card */}
            <div className="bg-[#F8F6F1] p-8 rounded-sm">
              <h3
                className="text-base font-bold text-[#1A1A1A] mb-6 pb-3 border-b border-gray-200"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                工事情報
              </h3>
              <ul className="space-y-4">
                {[
                  { icon: 'ri-building-line', label: '工事種別', value: p.category },
                  { icon: 'ri-map-pin-line', label: '施工場所', value: p.location },
                  { icon: 'ri-calendar-line', label: '竣工年', value: `${p.year}年` },
                  ...(p.client ? [{ icon: 'ri-user-line', label: '発注者', value: p.client }] : []),
                  ...(p.area ? [{ icon: 'ri-ruler-line', label: '施工面積', value: p.area }] : []),
                  ...(p.duration ? [{ icon: 'ri-time-line', label: '工期', value: p.duration }] : []),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#B8960C]/10 rounded-md flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#B8960C]`}></i>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 mb-0.5">{item.label}</div>
                      <div className="text-sm font-medium text-[#1A1A1A]">{item.value}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 bg-[#F8F6F1]">
        <div
          ref={highlightRef as React.RefObject<HTMLDivElement>}
          className="max-w-7xl mx-auto px-8"
          style={{
            opacity: highlightVisible ? 1 : 0,
            transform: highlightVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#B8960C]"></div>
              <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Highlights</span>
              <div className="w-8 h-px bg-[#B8960C]"></div>
            </div>
            <h2
              className="text-3xl lg:text-4xl font-bold text-[#1A1A1A]"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              施工のポイント
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {p.highlights.map((h, i) => (
              <div
                key={i}
                className="flex items-start gap-4 bg-white p-5 rounded-sm shadow-sm"
                style={{
                  opacity: highlightVisible ? 1 : 0,
                  transform: highlightVisible ? 'translateX(0)' : 'translateX(-20px)',
                  transition: `opacity 0.5s ease ${i * 0.1}s, transform 0.5s ease ${i * 0.1}s`,
                }}
              >
                <div className="w-7 h-7 flex items-center justify-center bg-[#B8960C] rounded-full flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-white text-xs"></i>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{h}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div
            ref={galleryRef as React.RefObject<HTMLDivElement>}
            className="text-center mb-14"
            style={{
              opacity: galleryVisible ? 1 : 0,
              transform: galleryVisible ? 'translateY(0)' : 'translateY(32px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#B8960C]"></div>
              <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Gallery</span>
              <div className="w-8 h-px bg-[#B8960C]"></div>
            </div>
            <h2
              className="text-3xl lg:text-4xl font-bold text-[#1A1A1A]"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              施工写真ギャラリー
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {p.galleryImages.map((img, i) => (
              <div
                key={i}
                className="group overflow-hidden rounded-sm shadow-md cursor-pointer"
                style={{
                  opacity: galleryVisible ? 1 : 0,
                  transform: galleryVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.97)',
                  transition: `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s`,
                }}
                onClick={() => setLightbox(img.src)}
              >
                <div className="w-full h-52 overflow-hidden relative">
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <i className="ri-zoom-in-line text-[#B8960C] text-lg"></i>
                    </div>
                  </div>
                </div>
                <div className="bg-white px-4 py-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 leading-relaxed">{img.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-px bg-[#B8960C]"></div>
            <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Contact Us</span>
            <div className="w-8 h-px bg-[#B8960C]"></div>
          </div>
          <h2
            className="text-3xl lg:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            同様の工事をご検討の方へ
          </h2>
          <p className="text-white/60 text-sm leading-relaxed mb-8">
            お見積り・ご相談は無料です。お気軽にお問い合わせください。
          </p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="/#contact"
              className="inline-flex items-center gap-3 bg-[#B8960C] text-white px-10 py-4 text-sm font-medium tracking-widest hover:bg-[#9A7D0A] transition-colors cursor-pointer whitespace-nowrap"
            >
              無料相談・お見積りはこちら
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>
            <a
              href={`/services/${p.relatedServiceSlug}`}
              className="inline-flex items-center gap-3 border border-white/40 text-white px-8 py-4 text-sm font-medium tracking-widest hover:border-[#B8960C] hover:text-[#B8960C] transition-colors cursor-pointer whitespace-nowrap"
            >
              サービス詳細を見る
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-8"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-white hover:text-[#B8960C] transition-colors cursor-pointer"
            onClick={() => setLightbox(null)}
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <img
            src={lightbox}
            alt="施工写真"
            className="max-w-full max-h-full object-contain rounded-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </main>
  );
}
